import CommonLookupDisplay from "components/CommonLookupDisplay";
import { GlobalContext, IModalFormElement } from "components/GlobalContext";
import { IColumnDefinition, createFilterableArrayColumnDefinition } from "components/SortableTable";
import IOpportunity, { IOpportunityCreateOrUpdate, IOpportunityFilters, IOpportunityInitResult } from "models/sql/IOpportunity";
import moment from "moment";
import { useContext, useEffect, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import useLocalStorage from "react-use-localstorage";
import OpportunityService from "services/OpportunityService";
import { formatCurrency, formatDate } from "utilities/StringUtility";
import { addNewLogEntry, applyGlobalFilterData, generateLog } from "utilities/MiscUtilities";
import { LinkContainer } from "react-router-bootstrap";
import { faEnvelope, faPhone, faListCheck, faMoneyCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

interface IOpportunitiesLookupProps {
    baseData?: IOpportunityInitResult;
}

const COUNTRIES_FILTER_KEY = "countries";
const STATES_FILTER_KEY = "states";

const OpportunitiesLookup = (props: IOpportunitiesLookupProps) => {
    const [filter, setFilter] = useState<IOpportunityFilters>({});
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const opportunityStages = ["Lead", "Qualify", "Active Demo", "Quoting", "Closed Won", "Closed Lost", "Demo Requested"];
    const [mode, setMode] = useLocalStorage(
        "opportunitiesLookupMode",
        searchParams.get("leads") == "true" ?
            "Leads" :
            searchParams.get("leads") == "false" ?
                "Opportunities" :
                searchParams.get("leads") == "closed" ?
                    "Closed" :
                    "");

    var getBaseFilter = () => {
        var result: any = {
            sortBys: ["OpportunityId"],
            sortDescending: true
        };

        switch (mode) {
            case "Leads":
                result = { ...result, leads: true, stages: undefined };
                break;
            case "Opportunities":
                result = { ...result, leads: false, stages: undefined };
                break;
            case "Closed":
                result = { ...result, stages: ["Closed Won", "Closed Lost"] };
                break;
            default:
                result = { ...result, stages: ["!Closed Won", "!Closed Lost"] };
                break;
        }

        return result;
    };

    useEffect(
        () => {
            if (JSON.stringify(filter) != "{}") {
                var newFilter = filter;
                switch (mode) {
                    case "Leads":
                        newFilter = { ...filter, stages: undefined, leads: true };
                        break;
                    case "Opportunities":
                        newFilter = { ...filter, stages: undefined, leads: false };
                        break;
                    case "Closed":
                        delete newFilter.leads;
                        newFilter = { ...filter, stages: ["Closed Won", "Closed Lost"] };
                        break;
                    default:
                        delete newFilter.leads;
                        newFilter = { ...newFilter, stages: ["!Closed Won", "!Closed Lost"] };
                        break;
                }

                setFilter(newFilter);
                setSearchParams(newFilter as any);
            }
        },
        [mode]);

    const showQuickNotes = (entry: IOpportunity, allData: IOpportunity[]) => {
        const formData: IModalFormElement[] = [{
            key: "Notes",
            type: "textarea",
            title: "Notes",
            value: entry.notes,
            style: { width: "100%", height: "200px" }
        }];

        globalContext.showModal({
            title: `Quick notes view`,
            cancelButtonTitle: "Close",
            formElements: formData,
            content: <></>,
            size: "lg",
            okButtonTitle: "Save Changes",
            okCallback: (data: any) => {
                let updateEntry = { ...entry };
                // @ts-ignore
                updateEntry.notes = data[0].value;
                updateEntry =
                    addNewLogEntry<IOpportunity>(
                        (new Date()),
                        `${globalContext.userName} updated notes`,
                        globalContext.userEmail,
                        101,
                        updateEntry);

                saveEntryUpdate(updateEntry, allData);
            }
        });
    };

    const showQuickLog = (entry: IOpportunity) => {
        globalContext.showModal({
            title: `Quick log view`,
            cancelButtonTitle: "Close",
            content: generateLog<IOpportunity>(entry),
            size: "lg"
        })
    };

    const saveEntryUpdate = async (entry: IOpportunity, allData: IOpportunity[]) => {
        globalContext.setLoading(true);
        const accessToken = await globalContext.getAccessTokenAsync();
        const data: IOpportunityCreateOrUpdate = {
            opportunityId: entry.opportunityId,
            created: entry.created,
            notes: entry.notes,
            extraData: entry.extraData,
            stage: entry.stage,
            country: entry.country,
            state: entry.state,
            fireDepartmentId: entry.fireDepartmentId,
            dealerId: entry.dealerId,
            meetingType: entry.meetingType,
            meetingStatus: entry.meetingStatus,
            meetingDate: entry.meetingDate,
            pocprimaryTitle: entry.pocprimaryTitle,
            pocprimaryName: entry.pocprimaryName,
            pocprimaryEmail: entry.pocprimaryEmail,
            pocprimaryPhone: entry.pocprimaryPhone,
            pocsecondaryTitle: entry.pocsecondaryTitle,
            pocsecondaryName: entry.pocsecondaryName,
            pocsecondaryEmail: entry.pocsecondaryEmail,
            pocsecondaryPhone: entry.pocsecondaryPhone,
            poctertiaryTitle: entry.poctertiaryTitle,
            poctertiaryName: entry.poctertiaryName,
            poctertiaryEmail: entry.poctertiaryEmail,
            poctertiaryPhone: entry.poctertiaryPhone,
            productInfoJson: entry.productInfoJson,
            healthRiskAssessmentId: entry.healthRiskAssessmentId,
            healthRiskAssessmentSentDate: entry.healthRiskAssessmentSentDate,
            airCardId: entry.airCardId,
            airCardSentDate: entry.airCardSentDate,
            fileAttachmentsJson: entry.fileAttachmentsJson,
            systemDataJson: entry.systemDataJson,
            lastUpdated: entry.lastUpdated,
            lastUpdatedBy: entry.lastUpdatedBy,
            createdBy: entry.createdBy,
            fundingStatus: entry.fundingStatus,
            salesRepId: entry.salesRepId,
            departmentName: entry.departmentName,
            station: entry.station,
            totalValue: entry.totalValue,
            qualified: entry.qualified,
            quotedTotalValue: entry.quotedTotalValue,
            quoteStatus: entry.quoteStatus,
            code: entry.code,
            source: entry.source,
            likleyhood: entry.likleyhood,
            timeline: entry.timeline
        };
        await OpportunityService.CreateOrUpdateAsync(accessToken, data);
        debugger;
        allData[allData.findIndex(x => x.opportunityId === data.opportunityId)] = data;
        globalContext.setLoading(false);
    };

    const exportTableDataAsync = async () => {
        const accessToken = await globalContext.getAccessTokenAsync();
        OpportunityService.DownloadExcelFileAsync(
            accessToken,
            "Opportunities.xlsx",
            applyGlobalFilterData(
                STATES_FILTER_KEY,
                COUNTRIES_FILTER_KEY,
                filter,
                globalContextRef.current.region,
                globalContextRef.current.organizationType));
    };

    const getColumns = (): IColumnDefinition<IOpportunity, IOpportunityFilters>[] => {
        return [
            {
                propName: "totalValue",
                title: "Estimated Value",
                customRenderer: (value, data) => <td className="align-top" key={`totalValue${data.opportunityId}`}>{formatCurrency(value ? parseFloat(value) : 0)}</td>,
                sortable: true
            },
            {
                propName: "quotedTotalValue",
                title: "Quoted Value",
                customRenderer: (value, data) => <td className="align-top" key={`quotedTotalValue${data.opportunityId}`}>{formatCurrency(value ? parseFloat(value) : 0)}</td>,
                sortable: true
            },
            {
                propName: "opportunityId",
                title: "Actions",
                customRenderer: (value, data, allData) => {
                    return (
                        <td key={`align-top actions${value}`}>
                            {
                                globalContext.roles.some(r => r === "Web.Admin") ?
                                    <>
                                        <span
                                            className="quick-action me-1"
                                            title="Quick note view"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                showQuickNotes(data, allData);
                                                return false;
                                            }}><FontAwesomeIcon icon={faMoneyCheck as IconProp} /></span>
                                        <span
                                            className="quick-action me-1"
                                            title="Activity log"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                showQuickLog(data);
                                                return false;
                                            }}><FontAwesomeIcon icon={faListCheck as IconProp} /></span>
                                    </> : <></>
                            }
                            {
                                data.pocprimaryEmail &&
                                <a
                                    className="me-2"
                                    href={`Mailto:${data.pocprimaryEmail}`}
                                    onClick={(e) => { e.stopPropagation(); }}>
                                    <FontAwesomeIcon icon={faEnvelope as IconProp} /></a>
                            }
                            {
                                data.pocprimaryPhone &&
                                <a
                                    className="me-2"
                                    href={`Tel:${data.pocprimaryPhone}`}
                                    onClick={(e) => { e.stopPropagation(); }}>
                                    <FontAwesomeIcon icon={faPhone as IconProp} /></a>
                            }
                        </td>)
                },
                sortable: false
            },
            {
                propName: "opportunityId",
                title: "Department",
                customRenderer: (value, data) => <td className="align-top" key={`department${data.opportunityId}`}>
                    {data?.fireDepartment?.fireDeptName}
                </td>,
                sortable: false
            },
            createFilterableArrayColumnDefinition<IOpportunity, IOpportunityFilters>(
                globalContext,
                // @ts-ignore
                "state",
                "states",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`state${row.opportunityId}`}>
                    {row.fireDepartment?.hqstate ?? ""}</td>,
                "State"),
            createFilterableArrayColumnDefinition<IOpportunity, IOpportunityFilters>(
                globalContext,
                "country",
                "countries",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`country${row.opportunityId}`}>
                    {row.fireDepartment?.hqCountry ?? row.country}</td>,
                "Country"),
            createFilterableArrayColumnDefinition<IOpportunity, IOpportunityFilters>(
                globalContext,
                "stage",
                "stages",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`stage${row.opportunityId}`}>
                    {value}</td>,
                "Stage"),
            createFilterableArrayColumnDefinition<IOpportunity, IOpportunityFilters>(
                globalContext,
                // @ts-ignore
                "dealerName",
                "dealerNames",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`dealerName${row.opportunityId}`}>
                    {row.dealer?.dealerName}
                </td>,
                "Dealer"),
            createFilterableArrayColumnDefinition<IOpportunity, IOpportunityFilters>(
                globalContext,
                // @ts-ignore
                "repName",
                "repNames",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`repName${row.opportunityId}`}>
                    {(!row.stage || row.stage == "Lead") && "New Lead / "}{
                        (row.salesRep ?
                            row.salesRep.fullName :
                            "No Rep Assigned")
                    }
                </td>,
                "Rep"),
            {
                propName: "created",
                title: "Created",
                customRenderer: (value, data) => <td className="align-top" key={`created${data.opportunityId}`}>
                    {data?.created ? formatDate(new Date(data?.created)) : ""}
                </td>,
                sortable: true
            },
            {
                propName: "opportunityId",
                title: "Source",
                customRenderer: (value, data) => <td className="align-top" key={`source${data.opportunityId}`}>
                    {data.airCardId && "AirCard"}
                    {data.airCardId && data.healthRiskAssessmentId && " & "}
                    {data.healthRiskAssessmentId && "HRA"}
                    {!data.airCardId && !data.healthRiskAssessmentId && "Manual"}
                </td>,
                sortable: false
            }
        ];
    };

    const rowStyleRenderer = (row: IOpportunity) => {
        let result = {};

        if (row.lastUpdated) {
            const diff =
                moment().diff(
                    moment(row.lastUpdated),
                    'days');

            if (diff > 35) {
                result = { backgroundColor: "rgba(255, 0, 0, 0.3)" };
            }
            else if (mode !== "Opportunities" && (row.airCard || row.healthRiskAssessment)) {
                if (
                    (row.airCard ? ((row.airCard.systemDataJson ?? "").indexOf("follow up email") > -1) : true) &&
                    (row.healthRiskAssessment ? ((row.healthRiskAssessment.systemDataJson ?? "").indexOf("follow up email") > -1) : true)
                ) {
                    result = { backgroundColor: "rgba(0, 255, 0, 0.1)" };
                }
                else {
                    result = { backgroundColor: "rgba(255, 0, 0, 0.1)" };
                }
            }
        }
        return result;
    };

    const getTools = () => [
        <>
            {
                globalContext.roles.some(r => r === "Web.Leads") && globalContext.roles.some(r => r === "Web.Opportunities") &&
                <Form.Select
                    style={{
                        "display": "inline",
                        "width": "130px",
                        "position": "relative",
                        "left": "-8px"
                    }}
                    className="float-start full-sm"
                    onChange={async (e) => {
                        setMode(e.target.value);
                    }}
                    value={mode}>
                    <option value={"Both"}>Both</option>
                    <option value={"Leads"}>Leads Only</option>
                    <option value={"Opportunities"}>Opp. Only</option>
                    <option value={"Closed"}>Closed</option>
                </Form.Select>
            }
        </>,
        <>
            {
                globalContext.roles.some(r => r === "Web.Opportunities") &&
                <LinkContainer to="/Tools/QuickQuote">
                    <Button
                        style={{
                            "display": "inline",
                            "width": "130px",
                            "position": "relative",
                            "left": "-8px"
                        }}
                        variant="primary"
                        className="ms-2 float-start full-sm"
                        onClick={() => {
                        }}>Quick Quote</Button>
                </LinkContainer>
            }
        </>,
        <Button
            className="me-2 full-sm"
            key="downloadExcel"
            onClick={() => { exportTableDataAsync(); }}>Export</Button>,
        <>
            {
                globalContext.roles.some(r => r === "Web.Admin") &&
                <Button
                    key="createnew"
                    variant="success"
                    className="full-sm"
                    onClick={() => { navigate(`/Opportunities/Detail/`) }}>Create New</Button>
            }
        </>
    ];

    return (
        <CommonLookupDisplay<IOpportunityFilters, IOpportunity, IOpportunityCreateOrUpdate>
            baseFilters={getBaseFilter()}
            filterHints={{
                leads: "Boolean",
                organizationTypes: "Array",
                hraIds: "Array",
                airCardIds: "Array",
                stages: "Array",
                sortBys: "Array",
                sortDescending: "Boolean"
            }}
            dataTypeName="Opportunity"
            dataTypeNamePlural="Opportunities"
            pathName="Opportunities"
            primaryIdKey="opportunityId"
            className=""
            countriesFilterKey={COUNTRIES_FILTER_KEY}
            statesFilterKey={STATES_FILTER_KEY}
            filter={filter}
            setFilter={setFilter}
            columnDefinitions={getColumns()}
            service={OpportunityService}
            tools={getTools()}
            rowStyleRenderer={rowStyleRenderer}
        />
    );
}

export default OpportunitiesLookup;
